/**
 * فرقِ دو نسخه‌ی یه سیگنال (مایگریشن 0072).
 *
 * ---------- چرا یه فایلِ تازه کنارِ features/admin/diff.ts ----------
 * اون یکی برای **ادمین** است و عمداً اسمِ خامِ فیلد رو نشون می‌ده
 * (`startsAt`، `venueName`)، چون کنارِ دادهٔ خام می‌شینه. این یکی رو
 * **دعوت‌شده** می‌خونه تا بفهمه «به کدوم نسخه اوکی دادم و از اون‌موقع چی
 * عوض شده». پس هر سطر یه برچسبِ ترجمه‌شده داره، نه نامِ ستون.
 *
 * و دو تفاوتِ دیگه که همون تفاوتِ مخاطب رو دنبال می‌کنن:
 *
 *   • **زمان یه سطره، نه پنج تا.** توی مدل، «کِی» از پنج فیلد ساخته می‌شه
 *     (`timeMode`, `startsAt`, `endsAt`, `dateOnly`, `approxLabel`) و عوض
 *     کردنِ یه تاریخ معمولاً چندتاشون رو با هم تکون می‌ده. پنج سطرِ «عوض شد»
 *     برای یه کارِ واحد، خبر نیست، سر و صداست.
 *   • **دسته‌بندی و زنجیره هم هستن.** برای ادمین بی‌ربطن، برای کسی که دعوت
 *     شده نه: «حالا دیگه می‌تونی دوستِ خودت رو هم بیاری» یه خبرِ واقعیه.
 *
 * این‌جا هیچ متنی ترجمه نمی‌شه؛ فقط **کلیدِ** i18n برمی‌گرده. دلیلش اینه که
 * ترجمه در لحظه‌ی رندر انجام می‌شه و اگه کاربر وسطِ کار زبان رو عوض کنه،
 * نتیجه‌ی کش‌شده‌ی این تابع نباید به زبانِ قبلی گیر کنه.
 */
import { signalWhenLabel } from './dates.ts';
import { formatApprox } from './approxLabel.ts';
import { t } from './i18n.ts';
import { tagText } from './tagLabel.ts';
import { kindMeta } from '../domain/signalKinds.ts';
import type { SignalKind, SignalVersion, Tag } from '../domain/types.ts';

export interface SignalFieldDiff {
  /** کلیدِ i18n برای نامِ فیلد. */
  labelKey: string;
  before: string;
  after: string;
}

/** «کِی» به‌شکلی که آدم می‌خونه — همون چیزی که روی کارتِ سیگنال هم می‌بینه. */
export const versionWhen = (v: SignalVersion): string => {
  if (v.startsAt) return signalWhenLabel(v);
  if (v.approxLabel) return formatApprox(v.approxLabel);
  return '';
};

/** null و undefined و رشته‌ی خالی همه یه چیزن؛ وگرنه هر بار یه «تغییر»ِ الکی. */
const norm = (v: unknown): string => (v == null ? '' : String(v).trim());

/**
 * فیلدهایی که بینِ دو نسخه فرق کردن. `before` نسخه‌ی قدیمی‌تره.
 *
 * دسته و زنجیره عمداً **خام** برمی‌گردن (شناسه‌ی تگ، عددِ زنجیره): معنی‌شون
 * به داده‌ای بستگی داره که این‌جا نداریمش (فهرستِ تگ‌ها) یا به برچسبی که
 * فقط UI می‌شناسه. صفحه‌ای که نشونشون می‌ده خودش ترجمه‌شون می‌کنه.
 */
export function diffSignalVersions(
  before: SignalVersion, after: SignalVersion,
): SignalFieldDiff[] {
  const rows: SignalFieldDiff[] = [];
  const push = (labelKey: string, a: string, b: string) => {
    if (a !== b) rows.push({ labelKey, before: a, after: b });
  };

  push('titleLabel', norm(before.title), norm(after.title));
  push('kind', norm(before.kind), norm(after.kind));
  push('when', versionWhen(before), versionWhen(after));
  push('placeLabel', norm(before.placeName), norm(after.placeName));
  push('costLabel', norm(before.costNote), norm(after.costNote));
  push('noteLabel', norm(before.note), norm(after.note));
  push('category', norm(before.categoryId), norm(after.categoryId));
  push('chainInvite', norm(before.chainLimit), norm(after.chainLimit));

  return rows;
}

/**
 * همون مقدارِ خام، به شکلی که آدم می‌خونه.
 *
 * تنها جای این فایل که ترجمه می‌کنه، و عمداً یه تابعِ **جدا**ست: خروجیِ
 * `diffSignalVersions` قابلِ کش کردنه چون به زبان بستگی نداره، و اگه ترجمه
 * توش می‌رفت، عوض کردنِ زبان یه فهرستِ کهنه به جا می‌ذاشت.
 *
 * توی هر دو اپ عینِ هم صدا زده می‌شه؛ اگه هر کدوم نسخه‌ی خودش رو داشت،
 * «دسته‌ی حذف‌شده» یه جا اسم داشت و اون یکی جا یه عددِ لخت.
 */
export function readableDiffValue(labelKey: string, raw: string, tags: Tag[] = []): string {
  if (!raw) return t('adminEmptyValue');
  if (labelKey === 'category') {
    const tag = tags.find((x) => String(x.id) === raw);
    return tag ? tagText(tag) : raw;
  }
  if (labelKey === 'chainInvite') {
    return raw === '0' ? t('chainOff')
      : raw === '1' ? t('chainPlus1')
        : raw === '2' ? t('chainPlus2') : t('chainUnlimited');
  }
  if (labelKey === 'kind') {
    const meta = kindMeta(raw as SignalKind);
    return `${meta.emoji} ${t(meta.titleKey)}`;
  }
  return raw;
}

/**
 * نسخه‌ای که کاربر روش جواب داده، و اینکه از اون‌موقع چیزی عوض شده یا نه.
 *
 * `respondedVersion` خالی یعنی هنوز جواب نداده — که «عوض شده» نیست، «هنوز
 * نگفته» است. جوابِ `pending` هم همینه: قاعده اینه که فقط کسی که یه بار
 * تصمیم گرفته، حق داره بفهمه تصمیمش قدیمی شده.
 */
export const answeredOnOldVersion = (
  s: { version: number; myResponse?: string | null; myRespondedVersion?: number | null },
): boolean => !!s.myRespondedVersion
  && !!s.myResponse
  && s.myResponse !== 'pending'
  && s.myRespondedVersion < s.version;
